import { type FormEvent, useEffect, useState } from "react";
import { AvatarOrInitials, Input, Button, FormField, Wordmark } from "@praesto/ui-kit";
import { enterOrg, requestCode, verifyHub } from "../lib/hub-client";
import { signInToTenant } from "../lib/auth-actions";

type Org = { slug: string; displayName: string };
type Step = "email" | "code" | "orgs" | "entering";

export function HubView({
  initialLinkToken,
  initialEmail,
}: {
  initialLinkToken?: string;
  initialEmail?: string;
}) {
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState(initialEmail ?? "");
  const [code, setCode] = useState("");
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [session, setSession] = useState("");
  const [pendingOnly, setPendingOnly] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function applyVerify(input: { email: string; code?: string; linkToken?: string }) {
    const r = await verifyHub(input);
    setSession(r.hubSession);
    setOrgs(r.orgs);
    setPendingOnly(r.pendingOnly);
    setStep("orgs");
  }

  // Magic-link entry: verify straight away with the token.
  useEffect(() => {
    if (initialLinkToken && initialEmail) {
      setBusy(true);
      applyVerify({ email: initialEmail, linkToken: initialLinkToken })
        .catch(() => setError("Link non valido o scaduto."))
        .finally(() => setBusy(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function submitEmail(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await requestCode(email);
      setStep("code");
    } catch {
      setError("Qualcosa è andato storto. Riprova.");
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await applyVerify({ email, code });
    } catch {
      setError("Codice non valido o scaduto.");
    } finally {
      setBusy(false);
    }
  }

  async function pick(slug: string) {
    setStep("entering");
    setError(null);
    try {
      const { customToken } = await enterOrg(session, slug);
      await signInToTenant(slug, customToken);
      window.location.replace(`/${slug}/home`);
    } catch {
      setError("Accesso non riuscito. Riprova.");
      setStep("orgs");
    }
  }

  return (
    <div className="min-h-screen bg-canvas text-fg-default flex items-center justify-center px-6">
      <div className="w-full max-w-sm">
        {/* The brand arrives as a file (C3F v18) — the mono "Praesto" eyebrow
            was the pre-v18 identity. */}
        <Wordmark height={34} />

        {step === "email" && (
          <form onSubmit={submitEmail}>
            <h1 className="mt-6 text-h3 font-semibold text-fg-strong">Accedi</h1>
            <p className="mt-3 text-sm text-fg-muted leading-relaxed">
              Inserisci la tua email: ti manderemo un codice per trovare le tue organizzazioni.
            </p>
            <div className="mt-6">
              <FormField label="Email">
                <Input
                  id="hub-email"
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </FormField>
            </div>
            {error && (
              <p className="mt-4 text-sm text-danger" role="alert">
                {error}
              </p>
            )}
            <Button
              type="submit"
              disabled={busy}
              variant="primary"
              size="lg"
              fullWidth
              className="mt-8"
            >
              {busy ? "Invio…" : "Invia codice"}
            </Button>
          </form>
        )}

        {step === "code" && (
          <form onSubmit={submitCode}>
            <h1 className="mt-6 text-h3 font-semibold text-fg-strong">Controlla l&apos;email</h1>
            <p className="mt-3 text-sm text-fg-muted leading-relaxed">
              Se <span className="text-fg-default">{email}</span> è registrata, ti abbiamo inviato
              un codice. Inseriscilo qui, oppure clicca il link nell&apos;email.
            </p>
            <div className="mt-6">
              <FormField label="Codice">
                <Input
                  id="hub-code"
                  inputMode="numeric"
                  pattern="\d{6}"
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
              </FormField>
            </div>
            {error && (
              <p className="mt-4 text-sm text-danger" role="alert">
                {error}
              </p>
            )}
            <Button
              type="submit"
              disabled={busy}
              variant="primary"
              size="lg"
              fullWidth
              className="mt-8"
            >
              {busy ? "Verifica…" : "Continua"}
            </Button>
          </form>
        )}

        {step === "orgs" && (
          <div>
            <h1 className="mt-6 text-h3 font-semibold text-fg-strong">Dove lavori oggi?</h1>
            {orgs.length === 0 ? (
              <p className="mt-4 text-sm text-fg-muted leading-relaxed">
                {pendingOnly
                  ? "Completa prima l'invito che ti abbiamo inviato per email, poi riprova."
                  : "Nessuna struttura è collegata a questa email. Controlla di aver usato l'email dell'invito, oppure chiedi un invito a chi gestisce la struttura."}
              </p>
            ) : (
              <>
                <p className="mt-2 text-sm text-fg-muted">Scegli dove entrare.</p>
                {/* Touchable org rows (reference: AuthScreens.jsx Hub) —
                    card surface, avatar, name, and the accent "Entra" cue. */}
                <ul className="mt-6 space-y-3">
                  {orgs.map((o) => (
                    <li key={o.slug}>
                      <button
                        type="button"
                        onClick={() => pick(o.slug)}
                        className="tap-target flex w-full items-center gap-4 rounded-lg border border-border-default bg-elevated px-4 py-3 text-left transition-colors duration-fast hover:bg-hover"
                      >
                        <AvatarOrInitials displayName={o.displayName} size={44} />
                        <span className="min-w-0 flex-1 truncate text-base font-semibold">
                          {o.displayName}
                        </span>
                        <span className="text-eyebrow shrink-0 text-accent-text">Entra →</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}
            {error && (
              <p className="mt-4 text-sm text-danger" role="alert">
                {error}
              </p>
            )}
          </div>
        )}

        {step === "entering" && (
          <p className="mt-8 text-caption text-fg-muted">Accesso in corso…</p>
        )}
      </div>
    </div>
  );
}
